import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { Order } from '../types';
import { nombreOperativo } from '../utils/productName';
import { BRAND } from '../data/brand';

// Colors from index.css
const PRIMARY_RGB: [number, number, number] = [72, 17, 29]; // #48111d
const SECONDARY_RGB: [number, number, number] = [243, 156, 18]; // #f39c12

type PrintableOrderItem = Order['Items'][number];

const getPromotionDetails = (item: PrintableOrderItem) => {
    if (item.DetallePromocion?.length) return item.DetallePromocion;
    if (item.PromoSelecciones?.length) return item.PromoSelecciones;
    return [];
};

const formatPromotionDetail = (component: ReturnType<typeof getPromotionDetails>[number]) =>
    `- ${component.grupo ? `${component.grupo}: ` : ''}${component.cantidad}x ${component.nombre}`;

// Helper for date formatting
const formatDateTime = (dateStr: string) => {
    try {
        const date = new Date(dateStr);
        return date.toLocaleString('es-AR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
        });
    } catch {
        return dateStr;
    }
};

export const generateOrderPDF = (order: Order) => {
    if (order.TipoPedido === 'Inmediato') {
        generateThermalTicket(order, 'TICKET', `Ticket_${order.ID}`);
    } else {
        // Para Envío y Retiro generamos 2 copias: Cliente y Local
        generateA4Receipt(order, 'CLIENTE', `Pedido_${order.ID}_cliente`);
        setTimeout(() => {
            generateA4Receipt(order, 'LOCAL / COCINA', `Pedido_${order.ID}_local`);
        }, 500);
    }
};

const generateThermalTicket = (order: Order, label: string = 'TICKET', fileName: string = 'Ticket') => {
    const promotionDetailCount = order.Items.reduce(
        (total, item) => total + getPromotionDetails(item).length,
        0
    );
    const estimatedHeight = 125 + (order.Items.length * 10) + (promotionDetailCount * 8);
    const doc = new jsPDF({
        unit: 'mm',
        format: [80, estimatedHeight]
    });

    const pageWidth = doc.internal.pageSize.getWidth();
    let y = 10;

    // Header
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 0, 0);
    doc.text(BRAND.upper, pageWidth / 2, y, { align: 'center' });
    y += 7;

    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);
    doc.text('Ticket de Pedido', pageWidth / 2, y, { align: 'center' });
    y += 8;

    // Order Info
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text(`Orden: #${order.ID}`, 5, y);
    y += 5;
    doc.text(`Fecha: ${formatDateTime(order.Fecha)}`, 5, y);
    y += 5;
    doc.text(`Cliente: ${order.Cliente || '-'}`, 5, y);
    y += 5;

    // Restricción de horario: resaltada en rojo (si aplica)
    if (order.RestriccionHorario) {
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(231, 76, 60);
        const splitRest = doc.splitTextToSize(`HORARIO: ${order.RestriccionHorario}`, 70);
        doc.text(splitRest, 5, y);
        y += (splitRest.length * 5);
        doc.setTextColor(0, 0, 0);
        doc.setFont('helvetica', 'normal');
    }
    y += 5;

    // Items Header
    doc.setFont('helvetica', 'bold');
    doc.text('Cant', 5, y);
    doc.text('Producto', 15, y);
    y += 3;
    doc.setDrawColor(0, 0, 0);
    doc.line(5, y, 75, y);
    y += 6; // Space after the header line

    // Items
    doc.setFont('helvetica', 'normal');
    order.Items.forEach(item => {
        doc.text(`${item.Cantidad}x`, 5, y);
        const splitName = doc.splitTextToSize(item.Producto, 45);
        doc.text(splitName, 15, y);

        if (item.Precio) {
            const subtotal = (item.Precio * item.Cantidad) - (item.Descuento || 0);
            doc.text(`$${subtotal.toLocaleString()}`, 75, y, { align: 'right' });
        }

        y += (splitName.length * 5);

        if (item.Descuento && item.Descuento > 0) {
            doc.setFontSize(7);
            doc.setTextColor(0, 0, 0);
            doc.text(`desc: -$${item.Descuento.toLocaleString()}`, 15, y);
            doc.setFontSize(9);
            y += 5;
        }

        const promotionDetails = getPromotionDetails(item);
        if (promotionDetails.length) {
            doc.setFontSize(7);
            doc.setFont('helvetica', 'bold');
            doc.text('Incluye (total):', 15, y);
            y += 4;
            doc.setFont('helvetica', 'normal');

            promotionDetails.forEach(component => {
                const componentLines = doc.splitTextToSize(formatPromotionDetail(component), 56);
                doc.text(componentLines, 17, y);
                y += componentLines.length * 3.5;
            });

            y += 2;
            doc.setFontSize(9);
        }
    });

    y += 2;
    doc.line(5, y, 75, y);
    y += 6;

    // Summary
    const subtotalGeneral = order.Items.reduce((acc, item) => acc + ((item.Precio || 0) * item.Cantidad), 0);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text(`Subtotal: $${subtotalGeneral.toLocaleString()}`, 75, y, { align: 'right' });
    y += 6;

    // Descuento total
    if (order.TotalDescuento && order.TotalDescuento > 0) {
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(0, 0, 0);
        doc.text(`DESCUENTO: -$${order.TotalDescuento.toLocaleString()}`, 75, y, { align: 'right' });
        y += 6;
    }

    // Summary
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text(`TOTAL: $${Number(order.Total).toLocaleString()}`, 75, y, { align: 'right' });
    y += 8;

    doc.setFontSize(9);
    doc.text(`Pago: ${order.MetodoPago}`, 5, y);
    y += 5;
    doc.text(`Estado: ${order.Pagado === 'Si' ? 'PAGADO' : 'PENDIENTE'}`, 5, y);
    y += 10;

    if (order.Observaciones) {
        doc.setFont('helvetica', 'italic');
        const splitObs = doc.splitTextToSize(`Obs: ${order.Observaciones}`, 70);
        doc.text(splitObs, 5, y);
        y += (splitObs.length * 5);
    }

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    y += 15;
    doc.text(`COPIA: ${label}`, pageWidth / 2, y, { align: 'center' });
    y += 5;
    doc.text('¡Gracias por su compra!', pageWidth / 2, y, { align: 'center' });

    // Abrir ventana de impresión y descargar
    doc.autoPrint();
    window.open(doc.output('bloburl'), '_blank');
    doc.save(`${fileName}.pdf`);
};

const generateA4Receipt = (order: Order, label: string = 'RECIBO', fileName: string = 'Pedido') => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    let y = 20;

    // Header
    doc.setFontSize(22);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(PRIMARY_RGB[0], PRIMARY_RGB[1], PRIMARY_RGB[2]);
    doc.text(BRAND.upper, 20, y);

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100);
    doc.text(`${label}`, pageWidth - 20, y, { align: 'right' });
    y += 10;

    doc.setDrawColor(SECONDARY_RGB[0], SECONDARY_RGB[1], SECONDARY_RGB[2]);
    doc.setLineWidth(1);
    doc.line(20, y, pageWidth - 20, y);
    y += 15;

    // Split Layout Start
    const leftColX = 20;
    const rightColX = 110;
    const topY = y;

    // Left Side: DATOS DEL PEDIDO
    doc.setFontSize(11);
    doc.setTextColor(0);
    doc.setFont('helvetica', 'bold');
    doc.text('DATOS DEL PEDIDO', leftColX, y);
    y += 7;
    doc.setFont('helvetica', 'normal');
    doc.text(`Número: #${order.ID}`, leftColX, y);
    y += 6;
    doc.text(`Tipo: ${order.TipoPedido}`, leftColX, y);
    y += 6;
    doc.text(`Método Pago: ${order.MetodoPago}`, leftColX, y);
    y += 6;
    doc.text(`Estado Pago: ${order.Pagado === 'Si' ? 'PAGADO' : 'PENDIENTE'}`, leftColX, y);

    // Right Side: DATOS DEL CLIENTE Y ENVÍO
    let ry = topY;
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('CLIENTE Y ENTREGA', rightColX, ry);
    ry += 7;
    doc.setFont('helvetica', 'normal');
    doc.text(`Nombre: ${order.Cliente}`, rightColX, ry);
    ry += 6;
    doc.text(`DNI: ${order.DNI || '-'}`, rightColX, ry);
    ry += 6;
    doc.text(`Tel: ${order.Telefono || '-'}`, rightColX, ry);
    ry += 6;
    doc.text(`Email: ${order.Email || '-'}`, rightColX, ry);
    ry += 9;

    // Shipping info inside right column
    if (order.Direccion || order.FechaEntrega) {
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(PRIMARY_RGB[0], PRIMARY_RGB[1], PRIMARY_RGB[2]);
        doc.text('DETALLES ENVÍO', rightColX, ry);
        ry += 7;
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(0);
        if (order.Direccion) {
            const splitDir = doc.splitTextToSize(`Dirección: ${order.Direccion}`, pageWidth - rightColX - 20);
            doc.text(splitDir, rightColX, ry);
            ry += (splitDir.length * 6);
        }
        if (order.FechaEntrega) {
            let formattedDate = order.FechaEntrega;
            const dateObj = new Date(formattedDate);
            if (!isNaN(dateObj.getTime()) && (formattedDate.includes('GMT') || formattedDate.includes('-'))) {
                const day = dateObj.getUTCDate().toString().padStart(2, '0');
                const months = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
                const monthName = months[dateObj.getUTCMonth()];
                const year = dateObj.getUTCFullYear();
                formattedDate = `${day} ${monthName} ${year}`;
            }
            const timeStr = order.HorarioEntrega ? ` de ${order.HorarioEntrega}` : '';
            doc.text(`Entrega: ${formattedDate}${timeStr}`, rightColX, ry);
            ry += 6;
        }
        // Restricción de horario: resaltada en rojo
        if (order.RestriccionHorario) {
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(231, 76, 60);
            const splitRest = doc.splitTextToSize(`HORARIO: ${order.RestriccionHorario}`, pageWidth - rightColX - 20);
            doc.text(splitRest, rightColX, ry);
            ry += (splitRest.length * 6);
            doc.setTextColor(0);
        }
    }

    // Adjust y to the max of left or right column
    y = Math.max(y + 10, ry + 10);

    // Items Table
    autoTable(doc, {
        startY: y,
        head: [['Producto', 'Cantidad', 'Precio Unit.', 'Descuento', 'Subtotal']],
        body: order.Items.map(item => {
            const promotionDetails = getPromotionDetails(item);
            return [
                promotionDetails.length
                    ? `${item.Producto}\nIncluye (total):\n${promotionDetails.map(formatPromotionDetail).join('\n')}`
                    : item.Producto,
                item.Cantidad,
                item.Precio ? `$${item.Precio.toLocaleString()}` : '-',
                item.Descuento && item.Descuento > 0 ? `-$${item.Descuento.toLocaleString()}` : '-',
                item.Precio ? `$${((item.Precio * item.Cantidad) - (item.Descuento || 0)).toLocaleString()}` : '-'
            ];
        }),
        theme: 'striped',
        headStyles: { fillColor: PRIMARY_RGB, textColor: [255, 255, 255] },
        columnStyles: {
            3: { textColor: [39, 174, 96], fontStyle: 'bold' }
        },
        margin: { left: 20, right: 20 }
    });

    y = (doc as any).lastAutoTable.finalY + 10;

    // Totals Grid
    const totalsX = pageWidth - 20;

    // Subtotal (Suma de productos antes de descuento)
    const itemsSubtotal = order.Items.reduce((acc, item) => acc + ((item.Precio || 0) * item.Cantidad), 0);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100);
    doc.text(`Subtotal: $${itemsSubtotal.toLocaleString()}`, totalsX, y, { align: 'right' });
    y += 6;

    // Descuento total
    if (order.TotalDescuento && order.TotalDescuento > 0) {
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(39, 174, 96);
        doc.text(`Descuento: -$${order.TotalDescuento.toLocaleString()}`, totalsX, y, { align: 'right' });
        doc.setTextColor(0, 0, 0);
        y += 6;
    }

    // Costo de envio
    if (order.CostoEnvio && order.CostoEnvio > 0) {
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(0);
        doc.text(`Costo de Envío: $${Number(order.CostoEnvio).toLocaleString()}`, totalsX, y, { align: 'right' });
        y += 8;
    }

    // TOTAL FINAL
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(PRIMARY_RGB[0], PRIMARY_RGB[1], PRIMARY_RGB[2]);
    doc.text(`TOTAL FINAL: $${Number(order.Total).toLocaleString()}`, totalsX, y, { align: 'right' });
    y += 15;

    if (order.Observaciones) {
        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(0);
        doc.text('Observaciones:', 20, y);
        y += 7;
        doc.setFont('helvetica', 'normal');
        const splitObs = doc.splitTextToSize(order.Observaciones, pageWidth - 40);
        doc.text(splitObs, 20, y);
    }

    // Footer
    doc.setFontSize(9);
    doc.setTextColor(150);
    doc.text('Este documento es un comprobante de pedido interno.', pageWidth / 2, doc.internal.pageSize.getHeight() - 15, { align: 'center' });

    // Abrir ventana de impresión y descargar
    doc.autoPrint();
    window.open(doc.output('bloburl'), '_blank');
    doc.save(`${fileName}.pdf`);
};


export const generateStockRoutinePDF = (products: any[], type: 'MAÑANA' | 'NOCHE') => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const hoy = new Date().toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' });

    const dayNames = ['DOMINGO', 'LUNES', 'MARTES', 'MIÉRCOLES', 'JUEVES', 'VIERNES', 'SÁBADO'];
    const dailyDuties: { [key: number]: string } = {
        1: "LUNES: Control de vencimientos cámara principal y limpieza estanterías A.",
        2: "MARTES: Verificación de etiquetas de precios y limpieza de heladeras mostrador.",
        3: "MIÉRCOLES: Control de stock de insumos (bolsas, papel, etiquetas) y limpieza sector B.",
        4: "JUEVES: Rotación de productos en freezer y control de sellado al vacío.",
        5: "VIERNES: Auditoría de productos frágiles y limpieza profunda de depósitos.",
        6: "SÁBADO: Control total de mercadería entrante y ordenamiento de stock de reserva."
    };

    // --- LÓGICA DE DIVISIÓN POR 6 DÍAS ---
    const totalSlices = 6;
    const itemsPerSlice = Math.ceil(products.length / totalSlices);

    // Iteramos de Lunes (1) a Sábado (6)
    for (let d = 1; d <= 6; d++) {
        if (d > 1) doc.addPage();

        let y = 15;
        const currentDayName = dayNames[d];

        // Header - Brand (Line 1)
        doc.setFontSize(22);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(PRIMARY_RGB[0], PRIMARY_RGB[1], PRIMARY_RGB[2]);
        doc.text(BRAND.upper, 20, y);
        y += 10;

        // Header - Title line (Line 2)
        doc.setFontSize(14);
        doc.setTextColor(SECONDARY_RGB[0], SECONDARY_RGB[1], SECONDARY_RGB[2]);
        doc.text(`PLANILLA DE STOCK ROTATIVO - ${type}`, 20, y);
        doc.setFontSize(12);
        doc.text(`DÍA: ${currentDayName}`, pageWidth - 20, y, { align: 'right' });
        y += 10;

        // Basic Info Row
        doc.setFontSize(10);
        doc.setTextColor(100);
        doc.setFont('helvetica', 'normal');
        doc.text(`Fecha Generación: ${hoy}`, 20, y);
        doc.text(`Responsable: __________________________`, pageWidth - 20, y, { align: 'right' });
        y += 8;

        doc.setDrawColor(PRIMARY_RGB[0], PRIMARY_RGB[1], PRIMARY_RGB[2]);
        doc.setLineWidth(0.5);
        doc.line(20, y, pageWidth - 20, y);
        y += 12;

        // --- SECCIÓN DE TAREAS SEMANALES ---
        doc.setFillColor(245, 245, 245);
        doc.rect(20, y - 5, pageWidth - 40, 15, 'F');
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(PRIMARY_RGB[0], PRIMARY_RGB[1], PRIMARY_RGB[2]);
        doc.setFontSize(9);
        const taskText = dailyDuties[d];
        const splitTask = doc.splitTextToSize(`TAREA DEL DÍA: ${taskText}`, pageWidth - 50);
        doc.text(splitTask, 25, y + 2);
        y += 20;

        // Slice products for the day
        const start = (d - 1) * itemsPerSlice;
        const end = start + itemsPerSlice;
        const productsToShow = products.slice(start, end);

        // Table
        autoTable(doc, {
            startY: y,
            head: [['Ítem #', 'Producto', 'Stk Ant.', 'Conteo', 'Dif.', 'Obs.']],
            body: productsToShow.map((p, index) => [
                start + index + 1, // Correlative number across the whole list
                p.NombresProductos ? nombreOperativo(p) : p.producto,
                '',
                '',
                '',
                ''
            ]),
            theme: 'grid',
            headStyles: {
                fillColor: PRIMARY_RGB,
                textColor: [255, 255, 255],
                fontSize: 9,
                halign: 'center'
            },
            styles: {
                cellPadding: 4,
                fontSize: 8,
                valign: 'middle'
            },
            columnStyles: {
                0: { cellWidth: 15, halign: 'center', fontStyle: 'bold' }, // Ítem (Ensanchado de 10 a 15)
                1: { cellWidth: 75 }, // Producto
                2: { cellWidth: 18, halign: 'center' }, // Stock Ant.
                3: { cellWidth: 18, halign: 'center' }, // Conteo Físico
                4: { cellWidth: 15, halign: 'center' }, // Diferencia
                5: { cellWidth: 'auto' }  // Observaciones
            },
            margin: { left: 20, right: 20 }
        });

        const finalY = (doc as any).lastAutoTable.finalY + 15;

        // Firma (Only if there is space, else next page is handle by autoTable)
        if (finalY < pageHeight - 30) {
            doc.setFontSize(10);
            doc.setTextColor(0);
            doc.text('Firma Responsable:', 20, finalY);
            doc.line(20, finalY + 5, 80, finalY + 5);

            doc.text('Hora de Cierre:', pageWidth - 60, finalY);
            doc.line(pageWidth - 60, finalY + 5, pageWidth - 20, finalY + 5);
        }

        // Footer
        doc.setFontSize(8);
        doc.setTextColor(150);
        const footerText = `Página ${d} de 6 | Bloque Semanal de Stock | ${BRAND.name}`;
        doc.text(footerText, pageWidth / 2, pageHeight - 10, { align: 'center' });
    }

    window.open(doc.output('bloburl'), '_blank');
    doc.save(`Planilla_Semanal_Rotativa_${type}_${hoy.replace(/\//g, '-')}.pdf`);
};

export const generateRemitoPDF = (transferData: {
    id: string,
    origen: string,
    destino: string,
    fecha: string,
    items: { producto: string, cantidad: number }[],
    usuario: string
}) => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    let y = 20;

    // Header
    doc.setFontSize(22);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(PRIMARY_RGB[0], PRIMARY_RGB[1], PRIMARY_RGB[2]);
    doc.text(BRAND.upper, 20, y);

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100);
    doc.text('REMITO DE TRASLADO', pageWidth - 20, y, { align: 'right' });
    y += 10;

    doc.setDrawColor(SECONDARY_RGB[0], SECONDARY_RGB[1], SECONDARY_RGB[2]);
    doc.setLineWidth(1);
    doc.line(20, y, pageWidth - 20, y);
    y += 15;

    // Info Sections
    doc.setFontSize(11);
    doc.setTextColor(0);
    doc.setFont('helvetica', 'bold');
    doc.text('DATOS DEL TRASLADO', 20, y);
    y += 7;
    doc.setFont('helvetica', 'normal');
    doc.text(`Identificador: #${transferData.id}`, 20, y);
    y += 6;
    doc.text(`Fecha: ${formatDateTime(transferData.fecha)}`, 20, y);
    y += 6;
    doc.text(`Responsable: ${transferData.usuario}`, 20, y);
    y += 10;

    // Route Info (Visual path)
    doc.setFillColor(245, 245, 245);
    doc.rect(20, y, pageWidth - 40, 20, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(PRIMARY_RGB[0], PRIMARY_RGB[1], PRIMARY_RGB[2]);
    doc.text('ORIGEN:', 25, y + 8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(0);
    doc.text(transferData.origen, 45, y + 8);

    doc.setFont('helvetica', 'bold');
    doc.setTextColor(PRIMARY_RGB[0], PRIMARY_RGB[1], PRIMARY_RGB[2]);
    doc.text('DESTINO:', 25, y + 15);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(0);
    doc.text(transferData.destino, 45, y + 15);
    y += 30;

    // Items Table
    autoTable(doc, {
        startY: y,
        head: [['Producto', 'Cantidad', 'Observaciones']],
        body: transferData.items.map(item => [
            item.producto,
            item.cantidad,
            '' // Espacio para notas manuales
        ]),
        theme: 'striped',
        headStyles: { fillColor: PRIMARY_RGB, textColor: [255, 255, 255] },
        styles: { fontSize: 10, cellPadding: 5 },
        columnStyles: {
            1: { halign: 'center', fontStyle: 'bold' }
        },
        margin: { left: 20, right: 20 }
    });

    const finalY = (doc as any).lastAutoTable.finalY + 30;

    // Signatures
    if (finalY < doc.internal.pageSize.getHeight() - 40) {
        doc.setFontSize(10);
        doc.text('__________________________', 40, finalY);
        doc.text('Firma Despacho', 50, finalY + 5);

        doc.text('__________________________', pageWidth - 80, finalY);
        doc.text('Firma Recepción', pageWidth - 70, finalY + 5);
    }

    // Footer
    doc.setFontSize(9);
    doc.setTextColor(150);
    doc.text('Este documento es un comprobante de traslado interno de mercadería.', pageWidth / 2, doc.internal.pageSize.getHeight() - 15, { align: 'center' });

    window.open(doc.output('bloburl'), '_blank');
    doc.save(`Remito_${transferData.id}.pdf`);
};